import type {
  QueryKey,
  UseSuspenseQueryResult,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  GetDashboardResult,
  GetPayoutsParams,
  GetPayoutsResult,
  GetServiceProviderResult,
  IServiceProviderPhoenixApi,
  UpdateServiceProviderParams,
  UpdateServiceProviderResult,
} from './api';
import { ServiceProviderPhoenixApi } from './api';

const ApiContext = createContext<IServiceProviderPhoenixApi | undefined>(
  undefined
);
const {
  useApi: useServiceProviderPhoenixApi,
  ApiProvider: ServiceProviderPhoenixApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ServiceProviderPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useServiceProviderPhoenixApi,
  ServiceProviderPhoenixApiProvider,
  ApiContext as ServiceProviderPhoenixApiContext,
  GetServiceProviderResult,
  GetPayoutsResult,
  GetDashboardResult,
};

export type { UpdateServiceProviderParams, UpdateServiceProviderResult };

export const GET_SERVICE_PROVIDER_PHOENIX_KEY: QueryKey = [
  'GET_SERVICE_PROVIDER_PHOENIX_KEY',
];

export const useGetServiceProviderPhoenix = (
  options?: Omit<
    UseSuspenseQueryOptions<GetServiceProviderResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<GetServiceProviderResult, unknown> => {
  return useSuspenseApiQuery(
    useServiceProviderPhoenixApi,
    GET_SERVICE_PROVIDER_PHOENIX_KEY,
    (api) => {
      return api.getServiceProvider();
    },
    options
  );
};

export const useGetServiceProviderPhoenixNoSuspense = (
  options?: Omit<
    UseQueryOptions<GetServiceProviderResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetServiceProviderResult, unknown> => {
  return useApiQuery(
    useServiceProviderPhoenixApi,
    GET_SERVICE_PROVIDER_PHOENIX_KEY,
    (api) => {
      return api.getServiceProvider();
    },
    options
  );
};

export const useGetDashboardPhoenix = (): UseQueryResult<
  GetDashboardResult,
  unknown
> => {
  return useApiQuery(
    useServiceProviderPhoenixApi,
    ['GET_DASHBOARD_PHOENIX'],
    (api) => api.getDashboard()
  );
};

export const useGetPayoutsPhoenix = (
  params: GetPayoutsParams
): UseQueryResult<GetPayoutsResult, unknown> => {
  return useApiQuery(
    useServiceProviderPhoenixApi,
    ['GET_PAYOUTS_PHOENIX'],
    (api) => api.getPayouts(params)
  );
};

export const useUpdateServiceProviderPhoenix = (): UseMutationResult<
  UpdateServiceProviderResult,
  unknown,
  UpdateServiceProviderParams
> => {
  const api = useServiceProviderPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateServiceProviderResult,
    unknown,
    UpdateServiceProviderParams
  >({
    mutationKey: ['update_service_provider_phoenix'],
    mutationFn: (data) =>
      api.updateServiceProvider({
        body: { data },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_SERVICE_PROVIDER_PHOENIX_KEY,
      });
    },
  });
};
